import { useForumType } from '@/components/hooks/useForumType';
import React, { useMemo } from "react";
import { registerComponent } from "../../lib/vulcan-lib/components";
import { Link } from "../../lib/reactRouterWrapper";
import { userGetProfileUrl } from "../../lib/collections/users/helpers";
import { AnalyticsContext } from "../../lib/analyticsEvents";
import { useRecommendations } from "./withRecommendations";
import { MAX_CONTENT_WIDTH } from "../posts/TableOfContents/ToCColumn";
import PostsLoading from "../posts/PostsLoading";
import UserTooltip from "../users/UserTooltip";
import PostsItem from "../posts/PostsItem";
import { useQuery } from "@/lib/crud/useQuery";
import { gql } from "@/lib/generated/gql-codegen";
import { RecommendationsAlgorithm } from "@/lib/collections/users/recommendationSettings";
import { defineStyles, useStyles } from "../hooks/useStyles";

const styles = defineStyles("PostBottomRecommendations", (theme: ThemeType) => ({
  root: {
    background: 'transparent',
    padding: "60px 0 80px 0",
    marginTop: 60,
    [theme.breakpoints.down('sm')]: {
      // make the background flush with the sides of the screen on mobile
      paddingLeft: 8,
      paddingRight: 8,
      marginLeft: -8,
      marginRight: -8,
    },
  },
  section: {
    maxWidth: MAX_CONTENT_WIDTH,
    margin: "0 auto 60px",
  },
  sectionHeading: {
    fontFamily: theme.palette.fonts.sansSerifStack,
    fontSize: 20,
    fontWeight: 600,
    color: theme.palette.grey[1000],
    marginBottom: 16,
  },
  largePostItem: {
    marginTop: 8,
  },
  viewMore: {
    marginTop: 12,
    fontFamily: theme.palette.fonts.sansSerifStack,
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.grey[600],
  },
}));

const PostBottomRecommendations = ({post, hasTableOfContents, ssr = false}: {
  post: PostsWithNavigation | PostsWithNavigationAndRevision | PostsList,
  hasTableOfContents?: boolean,
  ssr?: boolean,
}) => {
  const { isAF } = useForumType();
  const classes = useStyles(styles);
  const postId = post._id;
  const algorithm: RecommendationsAlgorithm = useMemo(() => ({
    strategy: {
      name: "moreFromAuthor",
      postId: postId,
      context: "post-footer",
    },
    count: 3,
    disableFallbacks: true,
    af: isAF,
  }), [postId, isAF]);

  const {
    recommendationsLoading: moreFromAuthorLoading,
    recommendations: moreFromAuthorPosts,
  } = useRecommendations({ algorithm, ssr });

  const { data: curatedAndPopularData, loading: curatedAndPopularLoading } = useQuery(gql(`
    query CuratedAndPopularThisWeek($limit: Int, $af: Boolean) {
      CuratedAndPopularThisWeek(limit: $limit, af: $af) {
        results {
          ...PostsListWithVotes
        }
      }
    }
  `), {
    variables: { limit: 3, af: isAF },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    ssr
  });

  const curatedAndPopularPosts = curatedAndPopularData?.CuratedAndPopularThisWeek?.results;

  const profileUrl = userGetProfileUrl(post.user);

  const postAuthor = post.user;
  const hasUserPosts = postAuthor && (moreFromAuthorLoading || !!moreFromAuthorPosts?.length);
  return (
    <AnalyticsContext pageSectionContext="postPageFooterRecommendations">
      <div className={classes.root}>
          <div>
            {hasUserPosts &&
              <div className={classes.section}>
                <div className={classes.sectionHeading}>
                  More from{" "}
                  <UserTooltip user={postAuthor} inlineBlock={false}>
                    <Link to={profileUrl}>{postAuthor.displayName}</Link>
                  </UserTooltip>
                </div>
                {moreFromAuthorLoading && !moreFromAuthorPosts?.length &&
                  <PostsLoading />
                }
                <AnalyticsContext pageSubSectionContext="moreFromAuthor">
                  {moreFromAuthorPosts?.map((post) => 
                    <PostsItem key={post._id} post={post} />
                  )}
                  <div className={classes.viewMore}>
                    <Link to={profileUrl}>
                      View more
                    </Link>
                  </div>
                </AnalyticsContext>
              </div>
            }
            <div className={classes.section}>
              <div className={classes.sectionHeading}>
                Curated and popular this week
              </div>
              {curatedAndPopularLoading && !curatedAndPopularPosts?.length &&
                <PostsLoading />
              }
              <AnalyticsContext pageSubSectionContext="curatedAndPopular">
                {curatedAndPopularPosts?.map((post) => <PostsItem key={post._id} post={post} />)}
              </AnalyticsContext>
            </div>
          </div>
      </div>
    </AnalyticsContext>
  );
}

export default registerComponent("PostBottomRecommendations", PostBottomRecommendations, {
  areEqual: "auto"
});
